import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment, useEffect, useState } from 'react';

import type { Country, Locale } from '@/types/regionTypes';

import { buildBreadcrumbFromContentful } from '@/components/Breadcrumb/helper';
import { NewsHeader } from '@/components/Cms/NewsHeader/NewsHeader';
import { StayTunedComponent } from '@/components/StayTunedNewsletter';
import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import type {
  ContentfulBreadcrumbFragment,
  ContentfulPageBreadcrumbFragment,
  ContentfulSelectorModalByMarketingPartnerQuery,
} from '@/graphql/contentful/api';
import { PageDocument } from '@/graphql/contentful/api';
import { useProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import type { ProductVariantsBySkusQuery } from '@/graphql/saleor/api';
import { useHeaderOffSet } from '@/hooks/useHeaderOffSet';
import type {
  CmsExtraDataRecord,
  ContentfulData,
  SectionsOpts,
} from '@/integrations/contentful';
import {
  generateSectionKey,
  isContentfulCardFragment,
  renderContentfulSection,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { Brand } from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';
import { mergeCmsDataAndProductInfo } from '@/lib/productSkus';
import { CmsViewStructuredData } from '@/lib/seo/StructuredData';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';

import MarketingSelectorModal from '../WatchView/components/MarketingSelectorModal/MarketingSelectorModal';

export interface CmsViewProps {
  breadcrumb?: ContentfulPageBreadcrumbFragment[] | null;
  cmsData: ContentfulData[];
  cmsExtraDataRecord: CmsExtraDataRecord;
  country: Country;
  draftMode: boolean | undefined;
  locale: Locale;
  marketingSelectorModalResult:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  pageTitle?: string;
  pageType?: string;
  productVariant?: ProductVariantsBySkusQuery | null;
  publishDate: string;
  shortDescription?: string;
  skuArray: string | string[];
  slug: string;
  tagsCollection: {
    __typename: string;
    items: Array<{
      value?: string | null;
    } | null>;
  };
}

export const CmsView = ({
  breadcrumb,
  cmsData,
  cmsExtraDataRecord,
  country,
  draftMode,
  locale,
  marketingSelectorModalResult,
  pageTitle,
  pageType,
  productVariant,
  publishDate,
  shortDescription,
  skuArray,
  slug,
  tagsCollection,
}: CmsViewProps) => {
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: PageDocument,
  });
  const liveBreadcrumb = useContentfulLiveUpdates(breadcrumb, {
    query: PageDocument,
  });
  const { query } = useRegionProvider();
  const headerHeight = useHeaderOffSet();
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        element.style.scrollMarginTop = `${headerHeight}px`;
      }
    }
  }, [headerHeight]);

  const [isMarketingSelectorModalOpen, setIsMarketingSelectorModalOpen] =
    useState(false);

  useEffect(() => {
    const handleOpenSelectorModal = () => {
      setIsMarketingSelectorModalOpen(true);
    };

    document.body.addEventListener(
      'openSelectorModal',
      handleOpenSelectorModal,
    );

    return () => {
      document.body.removeEventListener(
        'openSelectorModal',
        handleOpenSelectorModal,
      );
    };
  }, []);

  const { data: orderAppData } = useProductVariantsBusinessDetailsQuery({
    skip: !skuArray || skuArray.length === 0,
    variables: { skus: skuArray, channel: query.channel },
    notifyOnNetworkStatusChange: true,
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.orderApp,
    }),
  });

  const cmsDataWithProductInfo = mergeCmsDataAndProductInfo(
    liveCmsData,
    productVariant,
    orderAppData,
  );

  const breadcrumbItems = buildBreadcrumbFromContentful(liveBreadcrumb ?? []);
  const cmsSectionOpts: SectionsOpts = [
    {
      type: 'Card',
      opts: {
        items: breadcrumbItems,
        'data-test-id': 'CmsViewCard',
      },
    },
    {
      type: 'Breadcrumb',
      opts: {
        items: breadcrumbItems,
        'data-test-id': 'CmsViewBreadcrumb',
      },
    },
  ];

  return (
    <>
      {pageType === 'News' && (
        <NewsHeader
          breadcrumb={{
            items: breadcrumbItems,
            cms: cmsDataWithProductInfo.find(
              (item) => item.__typename === 'Breadcrumb',
            ) as ContentfulBreadcrumbFragment,
          }}
          pageTitle={pageTitle}
          publishDate={publishDate}
          shortDescription={shortDescription}
          tagsCollection={tagsCollection}
        />
      )}
      {cmsDataWithProductInfo
        ?.filter((item) =>
          pageType === 'News' ? item.__typename !== 'Breadcrumb' : true,
        )
        .map((data, i) => {
          const isGalletNewsletterCtaCard =
            (data as any).internalName === 'Newsletter - Stay tuned - CTA' &&
            isContentfulCardFragment(data) &&
            BRAND === Brand.Gallet;
          return (
            <Fragment key={generateSectionKey(data, i)}>
              {isGalletNewsletterCtaCard ? (
                <StayTunedComponent data={data} />
              ) : (
                renderContentfulSection(
                  data,
                  cmsSectionOpts,
                  cmsExtraDataRecord,
                  draftMode,
                )
              )}
            </Fragment>
          );
        })}
      <CmsViewStructuredData {...{ country, locale, cmsPageSlug: slug }} />

      <MarketingSelectorModal
        close={() => setIsMarketingSelectorModalOpen(false)}
        data-test-id='MarketingSelectorModal'
        isOpen={isMarketingSelectorModalOpen}
        marketingSelectorModalResult={marketingSelectorModalResult}
        onClose={() => setIsMarketingSelectorModalOpen(false)}
      />
    </>
  );
};

CmsView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
      pageType={page.props.pageType}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
